import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingCart, ChevronRight, Minus, Plus, CheckCircle2, Truck, Shield, RotateCcw, Baby } from 'lucide-react';

const TEXT_PRIMARY = 'text-[#3a2a32]';
const TEXT_BODY = 'text-[#5a4a52]';
const TEXT_MUTED = 'text-[#7a6670]';
const DISPLAY = { fontFamily: "'Quicksand', sans-serif", fontWeight: 700 };

const ProductDetailPage = () => {
  const { slug } = useParams();
  const { addToCart, openCart } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);

  const product = products.find(p => p.slug === slug);
  const category = product ? categories.find(c => c.id === product.categoryId) : null;
  const relatedProducts = product
    ? products.filter(p => p.categoryId === product.categoryId && p.id !== product.id).slice(0, 4)
    : [];

  useEffect(() => {
    if (product) document.title = `${product.name} | Tiny Wonders Pick`;
  }, [product]);

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <CartDrawer />
        <main className="pt-[104px]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 text-center">
            <Baby className="w-16 h-16 text-athletic-400 mx-auto mb-6" />
            <h1 className={`text-4xl ${TEXT_PRIMARY} mb-4`} style={DISPLAY}>Product Not Found</h1>
            <p className={`${TEXT_BODY} mb-8`}>The product you are looking for does not exist.</p>
            <Link to="/shop" className="inline-flex items-center gap-2 px-6 py-3 bg-athletic-500 text-white font-medium rounded-full hover:bg-athletic-600 transition-colors">
              Back to Shop
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const handleQuantityChange = (delta) => setQuantity(prev => Math.max(1, prev + delta));

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    openCart();
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="product-detail-page">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Breadcrumb */}
        <div className="bg-[#fdf4f7] border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4">
            <nav className="flex items-center gap-2 text-sm" data-testid="breadcrumb">
              <Link to="/" className={`${TEXT_MUTED} hover:text-athletic-600 transition-colors`}>Home</Link>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
              <Link to="/shop" className={`${TEXT_MUTED} hover:text-athletic-600 transition-colors`}>Shop</Link>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
              <Link to={`/shop?category=${category?.slug}`} className={`${TEXT_MUTED} hover:text-athletic-600 transition-colors`}>
                {category?.name}
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
              <span className={`${TEXT_PRIMARY} font-medium truncate max-w-[200px]`}>{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Product Details */}
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">

            {/* Product Image */}
            <div className="relative">
              <div className="aspect-square bg-[#fdf4f7] rounded-[2rem] overflow-hidden border border-gray-100">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
              </div>
              {product.badge && (
                <span className="absolute top-5 left-5 px-4 py-1.5 bg-white text-[#3a2a32] text-[10px] font-semibold uppercase tracking-[0.18em] rounded-full border border-gray-100 shadow-sm">
                  {product.badge}
                </span>
              )}
            </div>

            {/* Product Info */}
            <div>
              <Link
                to={`/shop?category=${category?.slug}`}
                className="inline-flex items-center gap-2 text-athletic-600 text-[11px] font-semibold uppercase tracking-[0.22em] hover:text-athletic-700 transition-colors mb-4"
                data-testid="product-category-link"
              >
                <Baby className="w-3.5 h-3.5" />
                {category?.name}
              </Link>

              <h1 className={`text-4xl lg:text-5xl ${TEXT_PRIMARY} mb-5 leading-[1.1]`} style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' }} data-testid="product-name">
                {product.name}
              </h1>

              <p className={`${TEXT_BODY} text-base leading-relaxed mb-8`} data-testid="product-description">
                {product.description}
              </p>

              {/* Price */}
              <div className="flex items-center gap-4 mb-10 pb-8 border-b border-gray-100">
                <span className={`text-4xl ${TEXT_PRIMARY}`} style={DISPLAY} data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                <span className="px-3 py-1.5 bg-athletic-50 text-athletic-700 text-xs font-medium rounded-full border border-athletic-200">
                  In Stock
                </span>
              </div>

              {/* Key Features */}
              {product.keyBenefits && (
                <div className="mb-10">
                  <h3 className={`text-xl ${TEXT_PRIMARY} mb-4`} style={DISPLAY}>Key Features</h3>
                  <ul className="space-y-3">
                    {product.keyBenefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-athletic-500 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                        <span className={`${TEXT_BODY} text-base leading-relaxed`}>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Care Instructions */}
              {product.usage && (
                <div className="mb-10 bg-[#fdf4f7] border border-gray-100 rounded-2xl p-6">
                  <h3 className={`text-xl ${TEXT_PRIMARY} mb-3`} style={DISPLAY}>Care Instructions</h3>
                  <p className={`${TEXT_BODY} text-sm leading-relaxed`}>{product.usage}</p>
                </div>
              )}

              {/* Features */}
              {product.features && (
                <div className="mb-10">
                  <div className="flex flex-wrap gap-2">
                    {product.features.map((feature, i) => (
                      <span key={i} className={`px-4 py-2 bg-white border border-gray-200 ${TEXT_BODY} text-xs font-medium tracking-wide rounded-full`}>
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity & Add to Cart */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <div className="flex items-center bg-white border border-gray-200 rounded-full overflow-hidden">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    className={`p-4 ${TEXT_MUTED} hover:bg-gray-50 transition-colors`}
                    data-testid="qty-decrement"
                    aria-label="Decrease quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className={`w-12 text-center ${TEXT_PRIMARY} font-medium`} data-testid="qty-value">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    className={`p-4 ${TEXT_MUTED} hover:bg-gray-50 transition-colors`}
                    data-testid="qty-increment"
                    aria-label="Increase quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  data-testid="add-to-cart-btn"
                  className={`flex-1 flex items-center justify-center gap-3 px-8 py-4 font-semibold tracking-wide rounded-full transition-all ${
                    addedToCart
                      ? 'bg-green-600 text-white'
                      : 'bg-athletic-500 hover:bg-athletic-600 text-white'
                  }`}
                >
                  {addedToCart ? (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Added to Cart
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </>
                  )}
                </button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Truck, title: 'Free Shipping', desc: 'On orders $65+' },
                  { icon: Shield, title: 'Secure Checkout', desc: 'SSL Protected' },
                  { icon: RotateCcw, title: '30-Day Returns', desc: 'Hassle-free' },
                ].map((badge, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-4 bg-[#fdf4f7] rounded-2xl border border-gray-100">
                    <badge.icon className="w-5 h-5 text-athletic-500 mb-2" strokeWidth={1.5} />
                    <span className={`${TEXT_PRIMARY} text-sm font-medium`}>{badge.title}</span>
                    <span className={`${TEXT_MUTED} text-xs mt-0.5`}>{badge.desc}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-24">
              <div className="flex items-end justify-between mb-10">
                <div>
                  <span className="text-athletic-600 text-[11px] font-semibold uppercase tracking-[0.22em]">You may also like</span>
                  <h2 className={`text-3xl lg:text-4xl ${TEXT_PRIMARY} mt-2`} style={DISPLAY}>
                    More to <span className="text-athletic-500">love</span>
                  </h2>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedProducts.map(relProduct => (
                  <Link
                    key={relProduct.id}
                    to={`/product/${relProduct.slug}`}
                    data-testid={`related-product-${relProduct.slug}`}
                    className="group block"
                  >
                    <div className="aspect-square bg-[#fdf4f7] rounded-2xl overflow-hidden mb-4 border border-gray-100">
                      <img
                        src={relProduct.image}
                        alt={relProduct.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <p className="text-[10px] text-athletic-600 uppercase tracking-[0.18em] font-semibold mb-1">
                      {category?.name}
                    </p>
                    <h3 className={`text-base ${TEXT_PRIMARY} mb-2 group-hover:text-athletic-600 transition-colors line-clamp-2 min-h-[44px]`}>
                      {relProduct.name}
                    </h3>
                    <p className={`text-base ${TEXT_PRIMARY}`} style={DISPLAY}>
                      ${relProduct.price.toFixed(2)}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetailPage;
